import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const input = "w-full bg-background border border-border px-3 py-2 text-sm outline-none focus:border-foreground transition-colors";
const labelCls = "text-[11px] tracking-[0.12em] uppercase text-muted-foreground block mb-1.5";
const EMPTY = { name: "", company: "", email: "", phone: "", address: "" };

export default function AdminCustomers() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/customers").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.name) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = { name: f.name, company: f.company, email: f.email, phone: f.phone, address: f.address };
      if (editing === "new") await api.post("/customers", payload);
      else await api.put(`/customers/${editing}`, payload);
      toast.success("Saved"); setEditing(null); load();
    } catch { toast.error("Failed to save"); } finally { setSaving(false); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this customer?")) return; await api.delete(`/customers/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="font-display font-extrabold text-3xl tracking-tight">Customers</h1><p className="text-muted-foreground text-sm mt-1">{items.length} customers</p></div>
        <button onClick={() => { setF(EMPTY); setEditing("new"); }} data-testid="new-customer-btn" className="flex items-center gap-2 bg-foreground text-background px-5 py-2.5 text-[12px] tracking-[0.1em] uppercase hover:bg-onyx-wood hover:text-onyx-black transition-colors"><Plus size={15} /> New Customer</button>
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-[10px] tracking-[0.12em] uppercase text-muted-foreground border-b border-border">
            <th className="text-left px-5 py-3 font-medium">Name</th><th className="text-left px-5 py-3 font-medium">Company</th><th className="text-left px-5 py-3 font-medium">Email</th><th className="text-left px-5 py-3 font-medium">Phone</th><th className="text-right px-5 py-3"></th>
          </tr></thead>
          <tbody className="divide-y divide-border">
            {items.map((c) => (
              <tr key={c.id} className="hover:bg-secondary/40" data-testid={`customer-row-${c.id}`}>
                <td className="px-5 py-4 font-medium">{c.name}</td><td className="px-5 py-4">{c.company}</td><td className="px-5 py-4">{c.email}</td><td className="px-5 py-4">{c.phone}</td>
                <td className="px-5 py-4"><div className="flex justify-end gap-1 text-muted-foreground">
                  <button onClick={() => { setF({ ...EMPTY, ...c }); setEditing(c.id); }} data-testid={`edit-customer-${c.id}`} className="p-2 hover:text-foreground"><Pencil size={16} /></button>
                  <button onClick={() => remove(c.id)} className="p-2 hover:text-destructive"><Trash2 size={16} /></button>
                </div></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="px-5 py-12 text-center text-muted-foreground">No customers yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-md bg-background" onClick={(e) => e.stopPropagation()} data-testid="customer-editor">
            <div className="border-b border-border px-6 h-16 flex items-center justify-between"><h2 className="font-display font-bold text-lg">{editing === "new" ? "New Customer" : "Edit Customer"}</h2><button onClick={() => setEditing(null)}><X /></button></div>
            <div className="p-6 space-y-4">
              {[["name", "Name"], ["company", "Company"], ["email", "Email"], ["phone", "Phone"], ["address", "Address"]].map(([k, l]) => (
                <div key={k}><label className={labelCls}>{l}</label><input data-testid={`customer-${k}`} className={input} value={f[k]} onChange={(e) => set(k, e.target.value)} /></div>
              ))}
              <button onClick={save} disabled={saving} data-testid="save-customer" className="w-full bg-foreground text-background py-3 text-[12px] tracking-[0.1em] uppercase hover:bg-onyx-wood hover:text-onyx-black transition-colors disabled:opacity-50">{saving ? "Saving…" : "Save Customer"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
